import * as Yup from "yup"

/**
 * Create Account — "Other" city (no Amok city_id).
 * API rule: each tier may send listed city_id OR *_other_address, never both.
 */

export const OTHER_CITY_VALUE = "__other__"

export const OTHER_CITY_OPTION = {
  value: OTHER_CITY_VALUE,
  label: "Other",
}

export const emptyOtherAddress = () => ({
  city: "",
  state: "",
  country: "",
})

export const isOtherCitySelection = cityId =>
  String(cityId || "") === OTHER_CITY_VALUE

export const withOtherCityOption = (options = []) => [
  ...options,
  OTHER_CITY_OPTION,
]

const normalizeOtherAddress = other => ({
  city: String(other?.city || "").trim(),
  state: String(other?.state || "").trim(),
  country: String(other?.country || "").trim(),
})

/**
 * Shape register body per API:
 * - listed city → only `*_address: { street, city_id }`
 * - Other → only `*_other_address: { city, state, country }`
 * - unused optional tier → omit both keys
 *
 * IMPORTANT: keep `is_adult` and `terms_accepted` (and other account fields)
 * in `...rest` — the API requires them. Never strip them as UI-only.
 */
export const buildRegisterPayload = values => {
  const {
    primary_address,
    secondary_address,
    tertiary_address,
    primary_other_address,
    secondary_other_address,
    tertiary_other_address,
    ...rest
  } = values || {}

  const payload = { ...rest }

  const applyTier = (tier, address, other) => {
    const addressKey = `${tier}_address`
    const otherKey = `${tier}_other_address`

    if (isOtherCitySelection(address?.city_id)) {
      payload[otherKey] = normalizeOtherAddress(other)
      return
    }

    const cityId = String(address?.city_id || "").trim()
    if (cityId) {
      payload[addressKey] = {
        street: address?.street || "",
        city_id: cityId,
      }
    }
  }

  applyTier("primary", primary_address, primary_other_address)
  applyTier("secondary", secondary_address, secondary_other_address)
  applyTier("tertiary", tertiary_address, tertiary_other_address)

  return payload
}

export const otherAddressWhenSelected = (addressPath, requiredMessagePrefix) =>
  Yup.object({
    city: Yup.string(),
    state: Yup.string(),
    country: Yup.string(),
  }).when(addressPath, {
    is: address => isOtherCitySelection(address?.city_id),
    then: schema =>
      schema.shape({
        city: Yup.string()
          .trim()
          .required(`${requiredMessagePrefix} city is required`),
        state: Yup.string()
          .trim()
          .required(`${requiredMessagePrefix} state / province is required`),
        country: Yup.string()
          .trim()
          .required(`${requiredMessagePrefix} country is required`),
      }),
  })
